import { FileImageOutlined } from "@ant-design/icons";
import { gql, useMutation } from "@apollo/client";
import { Alert, Button, Card, Col, Form, Input, Layout, Menu, Row, Space, Typography } from "antd";
import Dragger from "antd/lib/upload/Dragger";
import { omit } from "lodash";
import React, { Fragment, useCallback, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

const createCollectionBrandMutation = gql`
  mutation ($input: CreateCollectionBrandInput!) {
    createCollectionBrand(input: $input) {
      brand {
        id
      }
    }
  }
`;

interface FieldData {
  name: string;
  errors: string[];
}

export const New: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const navigate = useNavigate();

  const [execMutation, { loading }] = useMutation(createCollectionBrandMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      const fieldsErrors: FieldData[] = [];
      const otherErrors: string[] = [];
      graphQLErrors?.forEach((graphQLError) => {
        if (graphQLError.name) {
          fieldsErrors.push({ name: graphQLError.name, errors: (graphQLError as any).messages });
        } else {
          otherErrors.push(graphQLError.message);
        }
      });
      form.setFields(fieldsErrors);
      setOtherErrors(otherErrors);
    },
    onCompleted: (data) => {
      navigate("..");
    },
  });

  const valuesChangeHandler = useCallback(
    (changedValues) => {
      const fieldValues: any[] = [];
      if (changedValues.coverHorizontal && changedValues.coverHorizontal.file.status === "removed") {
        fieldValues.push({ name: ["coverHorizontal"], value: null });
      }
      if (changedValues.coverVertical && changedValues.coverVertical.file.status === "removed") {
        fieldValues.push({ name: ["coverVertical"], value: null });
      }
      form.setFields(fieldValues);
    },
    [form],
  );

  const onFinish = (values: any) => {
    const input = {
      ...omit(values, ["coverHorizontal", "coverVertical"]),
      coverHorizontal: values.coverHorizontal?.file,
      coverVertical: values.coverVertical?.file,
    };
    execMutation({ variables: { input } });
  };

  const uploadProps = {
    maxCount: 1,
    accept: "image/*",
    listType: "picture-card",
    beforeUpload: () => {
      return false;
    },
  };

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border">
              <Typography.Title level={4} style={{ lineHeight: "auto" }}>
                New Brand
              </Typography.Title>
            </Menu.Item>
          </Menu>
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border no-margin">
              <NavLink to="..">
                <Button>Cancel</Button>
              </NavLink>
            </Menu.Item>
            <Menu.Item className="no-border">
              <Button
                type="primary"
                loading={loading}
                onClick={() => {
                  form.submit();
                }}
              >
                Save
              </Button>
            </Menu.Item>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <Layout.Content className="content">
          <Card>
            {otherErrors.length > 0 && (
              <Space direction="vertical" style={{ marginBottom: "10px" }}>
                {otherErrors.map((otherError, index) => (
                  <Alert key={index} type="error" message="Error" description={otherError} />
                ))}
              </Space>
            )}
            <Form layout="vertical" form={form} onValuesChange={valuesChangeHandler} onFinish={onFinish}>
              <Row gutter={[24, 0]}>
                <Col span={20}>
                  <Form.Item name="code" label="Code" rules={[{ required: true, message: "is required" }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item name="position" label="Position" rules={[{ required: true, message: "is required" }]}>
                    <Input type="number" min={0} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="nameIt" label="Name IT" rules={[{ required: true, message: "is required" }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="nameEn" label="Name EN" rules={[{ required: true, message: "is required" }]}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="descriptionIt" label="Description IT">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="descriptionEn" label="Description EN">
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="coverHorizontal"
                    label="Cover horizontal"
                    rules={[{ required: true, message: "is required" }]}
                  >
                    <Dragger {...uploadProps}>
                      <div>
                        <FileImageOutlined style={{ fontSize: "30px" }} />
                        <div style={{ marginTop: 8 }}>Click or drop here</div>
                      </div>
                    </Dragger>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="coverVertical"
                    label="Cover vertical"
                    rules={[{ required: true, message: "is required" }]}
                  >
                    <Dragger {...uploadProps}>
                      <div>
                        <FileImageOutlined style={{ fontSize: "30px" }} />
                        <div style={{ marginTop: 8 }}>Click or drop here</div>
                      </div>
                    </Dragger>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="magoCode" label="MAGO Code" rules={[{ required: true, message: "is required" }]}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
